import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin, 
  Mail, 
  Phone, 
  MapPin,
  Heart
} from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Company',
      links: [
        { name: 'About Us', path: '/about' },
        { name: 'Services', path: '/services' },
        { name: 'Contact', path: '/contact' },
        { name: 'Download App', path: '/download' },
      ],
    },
    {
      title: 'Services',
      links: [
        { name: 'Consultation', path: '/services' },
        { name: 'Grooming', path: '/services' },
        { name: 'Vacation Care', path: '/services' },
        { name: 'Training', path: '/services' },
        { name: 'Dog Walking', path: '/services' },
        { name: 'Daycare', path: '/services' },
        { name: 'Boarding', path: '/services' },
      ],
    },
    {
      title: 'Support',
      links: [
        { name: 'Help Center', path: '/help' },
        { name: 'Privacy Policy', path: '/privacy' },
        { name: 'Terms of Service', path: '/terms' },
        { name: 'Shipping Policy', path: '/shipping' },
        { name: 'Cancellation & Refunds', path: '/cancellation-refunds' },
        { name: 'All Policies', path: '/policies' },
        { name: 'FAQ', path: '/faq' },
      ],
    },
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-secondary-800 text-white">
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="/logo-fetchtails.png" 
                  alt="FetchTails Logo" 
                  className="h-10 w-auto"
                />
                <span className="text-2xl font-bold">FetchTails</span>
              </div>
              <p className="text-gray-300 mb-2 leading-relaxed">
                Connecting pet owners with the best pet care services including consultation, grooming, training, walking, daycare, and boarding. 
                Your trusted partner in comprehensive pet care management.
              </p>
              <p className="text-gray-400 text-sm mb-6">
                <span className="font-semibold">Legal Entity:</span> YTP PRIVATE LIMITED
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-gray-300">
                  <Phone size={18} className="text-primary-400" />
                  <span>+91 9633651635</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <Mail size={18} className="text-primary-400" />
                  <span>yash@fetchtails.com</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <MapPin size={38} className="text-primary-400" />
                  <span className='text-m' ><b>Head Office:</b> 41 H I G, PRANAM 39/4564, PANAMPILLY NAGAR, PANAMPILLY NAGAR S.O., ERNAKULAM, KERALA-682036.</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <MapPin size={18} className="text-primary-400" />
                  <span><b>Branch Office:</b> Gurgaon, India</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <h3 className="text-lg font-semibold mb-4 text-white">
                {section.title}
              </h3>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-gray-300 hover:text-primary-400 transition-colors duration-300"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="border-t border-gray-700 mt-12 pt-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-2 text-gray-300">
              <span>© {currentYear} FetchTails. All rights reserved.</span>
              <Heart size={16} className="text-red-500 animate-pulse" />
            </div>
            
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="w-10 h-10 bg-gray-700 hover:bg-primary-500 rounded-full flex items-center justify-center transition-colors duration-300"
                  aria-label={social.label}
                >
                  <social.icon size={18} />
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer; 